/**
 * accountSettingsLink — the query shape a notification uses to land on one
 * tab of the ACCOUNT settings after login:
 *
 *   ?view=dashboard:location&acc=<account>&account_tab=partners
 *     &partner_connection=<connection>&partner_tab=service-codes
 *
 * (the "partner service codes to match" email / magic link). `acc` and
 * `account_tab` are required; the partner pair is optional and only means
 * something with account_tab=partners. Any view may carry it — the account
 * settings open over whatever is showing.
 *
 * `account_subtab` names the sub-tab inside the tab (People › Roles, Time &
 * Attendance › Pay Rules). The open dialog writes this shape itself
 * (withAccountSettingsLink) as it opens and as its tabs change, so Back closes
 * it and a reload opens it again on the same tab.
 *
 * `account_section` goes one level further, into a sub-tab that has inner
 * tabs of its own: `account_tab=financials&account_subtab=invoicing
 * &account_section=options` is Financials › Invoicing › Options (where
 * "Declined services" lives). It only means something with its sub-tab; the
 * container ignores an id it does not know.
 */
export interface AccountSettingsLink {
  accountId: string;
  tab: string;
  subTab?: string;
  /** Inner section of the sub-tab (Invoicing › Options). */
  section?: string;
  partnerConnectionId?: string;
  partnerTab?: 'locations' | 'service-codes' | 'settings';
}

const PARTNER_TABS = ['locations', 'service-codes', 'settings'] as const;

export function parseAccountSettingsLink(search: string | URLSearchParams): AccountSettingsLink | null {
  const params = typeof search === 'string' ? new URLSearchParams(search.replace(/^[?&]/, '')) : search;
  const accountId = params.get('acc')?.trim();
  const tab = params.get('account_tab')?.trim();
  if (!accountId || !tab) return null;
  const subTab = params.get('account_subtab')?.trim() || undefined;
  const section = (subTab && params.get('account_section')?.trim()) || undefined;
  const partnerConnectionId = params.get('partner_connection')?.trim() || undefined;
  const rawPartnerTab = params.get('partner_tab')?.trim();
  const partnerTab = (PARTNER_TABS as readonly string[]).includes(rawPartnerTab ?? '')
    ? (rawPartnerTab as AccountSettingsLink['partnerTab'])
    : undefined;
  return { accountId, tab, ...(subTab ? { subTab } : {}), ...(section ? { section } : {}), partnerConnectionId, partnerTab };
}

/** Whether the URL names this account's settings dialog (any tab). */
export function describesAccountSettings(search: string | URLSearchParams, accountId: string): boolean {
  return parseAccountSettingsLink(search)?.accountId === accountId;
}

/**
 * The same URL naming this account's settings on this tab. The rest of the
 * query is kept. A partner connection target belongs to the notification that
 * opened Partners on it; once the dialog moves on it is dropped.
 */
export function withAccountSettingsLink(search: string, link: AccountSettingsLink): string {
  const params = new URLSearchParams(search.replace(/^[?&]/, ''));
  params.set('acc', link.accountId);
  params.set('account_tab', link.tab);
  if (link.subTab) params.set('account_subtab', link.subTab);
  else params.delete('account_subtab');
  if (link.subTab && link.section) params.set('account_section', link.section);
  else params.delete('account_section');
  if (link.tab === 'partners' && link.partnerConnectionId) {
    params.set('partner_connection', link.partnerConnectionId);
    if (link.partnerTab) params.set('partner_tab', link.partnerTab);
    else params.delete('partner_tab');
  } else {
    params.delete('partner_connection');
    params.delete('partner_tab');
  }
  return `?${params.toString()}`;
}

/** The same URL without the account settings target (closed; back/reload should not reopen it). */
export function stripAccountSettingsLink(search: string): string {
  const params = new URLSearchParams(search.replace(/^[?&]/, ''));
  params.delete('account_tab');
  params.delete('account_subtab');
  params.delete('account_section');
  params.delete('partner_connection');
  params.delete('partner_tab');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** A stable identity for one link, so the same link is followed once. */
export function accountSettingsLinkKey(link: AccountSettingsLink): string {
  return [link.accountId, link.tab, link.subTab ?? '', link.section ?? '', link.partnerConnectionId ?? '', link.partnerTab ?? ''].join('|');
}

export interface FollowAccountSettingsLinkDeps {
  /** The app's one account switch (hooks/useAccountSwitch performAccountSwitch). */
  switchAccount: (accountId: string) => Promise<{ ok: boolean; error?: string }>;
  /** Show the account settings. */
  open: (link: AccountSettingsLink) => void;
  /** The switch failed: say so and drop the link. */
  fail: (message: string) => void;
}

/**
 * Follow a link into an account's settings: re-scope the session to that
 * account FIRST (cookie, location, permissions — the same switch the top bar
 * uses), and open the settings only once that succeeded. Opening straight
 * away would send the modal's requests under whatever account the session
 * was on, and a notification can name a different one.
 */
export async function followAccountSettingsLink(link: AccountSettingsLink, deps: FollowAccountSettingsLinkDeps): Promise<boolean> {
  const result = await deps.switchAccount(link.accountId);
  if (!result.ok) {
    deps.fail(result.error || 'Could not open that account');
    return false;
  }
  deps.open(link);
  return true;
}

/**
 * Bookkeeping for following settings links one account switch at a time
 * (App.tsx holds one per session). performAccountSwitch refuses a second
 * switch while one is in flight, so links never start switches concurrently.
 */
export interface AccountSettingsLinkFollower {
  /** Key of the newest link asked for (skips re-following it on every URL sync). */
  key: string | null;
  /** The newest link asked for: the one that opens when its account's switch lands. */
  latest: AccountSettingsLink | null;
  /** The switch in flight (or queued behind one), and its account. */
  pending: Promise<boolean> | null;
  pendingAccountId: string | null;
  /** Key of the link last opened, so a queued switch does not re-open (or re-switch for) it. */
  openedKey: string | null;
}

export const createAccountSettingsLinkFollower = (): AccountSettingsLinkFollower => ({
  key: null,
  latest: null,
  pending: null,
  pendingAccountId: null,
  openedKey: null,
});

/** The link was dropped (closed or failed): a switch still in flight opens nothing. */
export function resetAccountSettingsLinkFollower(follower: AccountSettingsLinkFollower): void {
  follower.key = null;
  follower.latest = null;
  follower.openedKey = null;
}

function queueAccountSwitch(
  follower: AccountSettingsLinkFollower,
  accountId: string,
  deps: FollowAccountSettingsLinkDeps
): Promise<boolean> {
  const after = follower.pending;
  // Assigned before the body first reads it: every read follows an await.
  let run!: Promise<boolean>;
  run = (async () => {
    // Wait out the switch in flight; never call switchAccount concurrently.
    if (after) await after.catch(() => false);
    // A newer link for another account took over while we waited — or an
    // earlier switch to this account already opened the newest link.
    const waitedFor = follower.latest;
    if (waitedFor?.accountId !== accountId || follower.openedKey === accountSettingsLinkKey(waitedFor)) {
      if (follower.pending === run) follower.pending = follower.pendingAccountId = null;
      return false;
    }
    const result = await deps.switchAccount(accountId);
    if (follower.pending === run) follower.pending = follower.pendingAccountId = null;
    const latest = follower.latest;
    // Superseded (another account, or dropped) while switching: open nothing.
    if (!latest || latest.accountId !== accountId) return false;
    if (!result.ok) {
      deps.fail(result.error || 'Could not open that account');
      return false;
    }
    follower.openedKey = accountSettingsLinkKey(latest);
    deps.open(latest);
    return true;
  })();
  follower.pending = run;
  follower.pendingAccountId = accountId;
  return run;
}

/**
 * Follow a link unless it is the one already being followed (App.tsx re-runs
 * its URL sync on every navigation). The newest link wins:
 *  - same account as the switch in flight (only account_subtab /
 *    account_section changed): no second switch — the pending one opens the
 *    newest link when it lands;
 *  - another account: its switch is queued behind the pending one, and an
 *    older link superseded meanwhile opens nothing.
 * Returns null when skipped.
 */
export function followAccountSettingsLinkOnce(
  follower: AccountSettingsLinkFollower,
  link: AccountSettingsLink,
  deps: FollowAccountSettingsLinkDeps
): Promise<boolean> | null {
  const key = accountSettingsLinkKey(link);
  if (follower.key === key) return null;
  follower.key = key;
  follower.latest = link;
  if (follower.pending && follower.pendingAccountId === link.accountId) return follower.pending;
  return queueAccountSwitch(follower, link.accountId, deps);
}
